import { useEffect, useRef, useState } from 'react'
import { Shield, FileText, CheckCircle2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'

export interface LegalDocument {
  type: string
  title: string
  content: string
  version: number
  last_updated: string
}

export interface EmbeddedLegalConsentModalProps {
  isOpen: boolean
  onAccept: () => void
  onClose?: () => void
  termsDocument: LegalDocument
  privacyDocument: LegalDocument
}

export function EmbeddedLegalConsentModal({
  isOpen,
  onAccept,
  onClose,
  termsDocument,
  privacyDocument
}: EmbeddedLegalConsentModalProps) {
  const [activeTab, setActiveTab] = useState('terms')
  const termsContentRef = useRef<HTMLDivElement>(null)
  const privacyContentRef = useRef<HTMLDivElement>(null)
  const acceptButtonRef = useRef<HTMLButtonElement>(null)

  // Focus management for accessibility
  useEffect(() => {
    if (isOpen && acceptButtonRef.current) {
      const timer = setTimeout(() => {
        acceptButtonRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Prevent closing modal by clicking outside or ESC (force user to accept)
  const handleOpenChange = (open: boolean) => {
    if (!open && onClose) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        showCloseButton={false}
        className="
          !w-[96vw] sm:!w-[92vw] md:!w-[88vw] lg:!w-[75vw] xl:!w-[70vw]
          !max-w-[1100px]
          !min-w-[320px]
          !h-[90dvh] sm:!h-[88vh] md:!h-[82vh]
          !max-h-[92vh]
          !flex !flex-col
          !p-0
          !gap-0
          !rounded-xl sm:!rounded-2xl
          !overflow-hidden
          !bg-white
          !border
        "
        onEscapeKeyDown={onClose ? undefined : (e) => e.preventDefault()}
        onPointerDownOutside={onClose ? undefined : (e) => e.preventDefault()}
      >
        {/* Visually hidden title and description for accessibility (required by Radix UI) */}
        <DialogTitle className="sr-only">
          Legal Documents - Terms of Service and Privacy Policy
        </DialogTitle>
        <DialogDescription className="sr-only">
          Please review and accept the Terms of Service and Privacy Policy to continue.
        </DialogDescription>
        <p id="legal-consent-description" className="sr-only">Please review and accept our Terms of Service and Privacy Policy to continue.</p>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Sticky Tabs - Always visible at top */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 py-3 md:py-4 flex-shrink-0">
              <TabsList className="grid w-full grid-cols-2 h-auto p-1 bg-gray-100 rounded-xl">
                <TabsTrigger 
                  value="terms" 
                  className="
                    flex items-center justify-center gap-2 
                    py-3 md:py-3.5 
                    text-sm md:text-base 
                    font-medium 
                    rounded-lg 
                    transition-all 
                    data-[state=active]:bg-white 
                    data-[state=active]:shadow-md 
                    data-[state=active]:text-gray-900
                    data-[state=inactive]:text-gray-600
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-0
                  "
                  aria-label="View Terms of Service"
                >
                  <FileText className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" aria-hidden="true" />
                  <span>Terms</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="privacy" 
                  className="
                    flex items-center justify-center gap-2 
                    py-3 md:py-3.5 
                    text-sm md:text-base 
                    font-medium 
                    rounded-lg 
                    transition-all 
                    data-[state=active]:bg-white 
                    data-[state=active]:shadow-md 
                    data-[state=active]:text-gray-900
                    data-[state=inactive]:text-gray-600
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-0
                  "
                  aria-label="View Privacy Policy"
                >
                  <Shield className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" aria-hidden="true" />
                  <span>Privacy</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Terms of Service Content */}
            <TabsContent 
              value="terms" 
              className="flex-1 min-h-0 mt-0 data-[state=active]:flex data-[state=active]:flex-col focus-visible:outline-none"
              tabIndex={-1}
            >
              <div 
                ref={termsContentRef}
                className="
                  flex-1 
                  min-h-0
                  px-4 sm:px-6 md:px-10 lg:px-12
                  py-4 sm:py-5 md:py-6
                  overflow-y-auto 
                  overscroll-contain
                  bg-white 
                  scroll-smooth
                  touch-pan-y
                "
              >
                <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none md:max-w-3xl md:mx-auto
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900
                  prose-table:border-collapse prose-th:bg-gray-50 prose-td:border prose-th:border
                  prose-th:border-gray-200 prose-td:border-gray-200 prose-th:px-4 prose-th:py-2 prose-td:px-4 prose-td:py-2">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                    {termsDocument.content}
                  </ReactMarkdown>
                </div>
              </div>
            </TabsContent>

            {/* Privacy Policy Content */}
            <TabsContent 
              value="privacy" 
              className="flex-1 min-h-0 mt-0 data-[state=active]:flex data-[state=active]:flex-col focus-visible:outline-none"
              tabIndex={-1}
            >
              <div 
                ref={privacyContentRef}
                className="
                  flex-1 
                  min-h-0
                  px-4 sm:px-6 md:px-10 lg:px-12
                  py-4 sm:py-5 md:py-6
                  overflow-y-auto 
                  overscroll-contain
                  bg-white 
                  scroll-smooth
                  touch-pan-y
                "
              >
                <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none md:max-w-3xl md:mx-auto
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900
                  prose-table:border-collapse prose-th:bg-gray-50 prose-td:border prose-th:border
                  prose-th:border-gray-200 prose-td:border-gray-200 prose-th:px-4 prose-th:py-2 prose-td:px-4 prose-td:py-2">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                    {privacyDocument.content}
                  </ReactMarkdown>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Fixed Footer */}
        <DialogFooter className="
          flex-shrink-0 
          px-4 sm:px-6 md:px-8
          py-4 md:py-5
          bg-gray-50
          border-t border-gray-200 
        ">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
            {/* Consent text */}
            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed text-center sm:text-left">
              By clicking "I Accept", you agree to our{' '}
              <span className="font-medium text-gray-700">Terms of Service</span> and{' '}
              <span className="font-medium text-gray-700">Privacy Policy</span>.
            </p>

            {/* Accept Button - Full width on mobile, right-aligned on desktop */}
            <Button
              ref={acceptButtonRef}
              onClick={onAccept}
              className="
                w-full sm:w-auto
                !bg-orange-500 !text-white
                !hover:bg-orange-600
                !active:bg-orange-700
                px-8 md:px-10
                py-3.5 md:py-3
                text-base md:text-sm
                font-semibold
                min-h-[52px] sm:min-h-[44px]
                rounded-xl sm:rounded-lg
                shadow-lg shadow-orange-500/25
                transition-all duration-200
                hover:shadow-xl hover:shadow-orange-500/30
                active:scale-[0.98]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2
                touch-manipulation
              "
              aria-label="Accept Terms of Service and Privacy Policy"
            >
              <CheckCircle2 className="h-5 w-5 mr-2" aria-hidden="true" />
              I Accept
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
